import { useState } from "react";
import {
  Card,
  Typography,
  Alert,
  Tag,
  Timeline,
  Divider,
  Space,
  Badge,
  Tooltip,
  Button,
  Collapse,
  Row,
  Col,
} from "antd";
import {
  CheckCircleFilled,
  WarningFilled,
  InfoCircleFilled,
  ShopOutlined,
  ExperimentOutlined,
  ImportOutlined,
  DollarOutlined,
  AuditOutlined,
  BarChartOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  ArrowRightOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface StepDetail {
  key: number;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color: string;
  bgColor: string;
  description: string;
  actions: string[];
  warnings?: { type: "warning" | "error" | "info"; message: string }[];
  example?: React.ReactNode;
}

// ─── DATA ────────────────────────────────────────────────────────────────────

const STEPS: StepDetail[] = [
  {
    key: 0,
    icon: <ShopOutlined />,
    title: "Khai báo đơn vị tính",
    subtitle: "Nền tảng cho toàn bộ hệ thống",
    color: "#5b8dee",
    bgColor: "#eff4ff",
    description:
      "Tạo các đơn vị đo lường sẽ dùng cho nguyên vật liệu như kg, lít, gói, lon, hộp... " +
      "Bước này cần làm trước tiên vì đơn vị tính là thông tin bắt buộc khi khai báo nguyên liệu.",
    actions: [
      "Vào menu Danh mục → Đơn vị tính",
      "Nhấn Thêm mới và nhập tên đơn vị (VD: kg, lít, gói)",
      "Lặp lại cho tất cả đơn vị cần dùng",
    ],
    example: (
      <Space wrap>
        {["kg", "lít", "gói", "hộp", "lon", "thùng", "túi"].map((u) => (
          <Tag key={u} color="blue" style={{ borderRadius: 20, padding: "2px 12px" }}>
            {u}
          </Tag>
        ))}
      </Space>
    ),
  },
  {
    key: 1,
    icon: <ExperimentOutlined />,
    title: "Khai báo nguyên vật liệu",
    subtitle: "Danh sách nguyên liệu dùng để pha chế",
    color: "#13c2c2",
    bgColor: "#e6fffb",
    description:
      "Tạo danh sách tất cả nguyên liệu dùng trong quán, gắn với đơn vị tính tương ứng. " +
      "Danh sách này là cơ sở để nhập kho và lập phiếu kiểm kê — thiếu nguyên liệu nào " +
      "thì phiếu kiểm kê sẽ không tính được tồn của nguyên liệu đó.",
    actions: [
      "Vào menu Danh mục → Nguyên vật liệu",
      "Nhấn Thêm mới, nhập tên nguyên liệu và chọn đơn vị tính",
      "Thêm đầy đủ tất cả nguyên liệu — kể cả loại ít dùng",
    ],
    example: (
      <Space direction="vertical" style={{ width: "100%" }} size={4}>
        {[
          ["Trà đen Assam", "kg"],
          ["Sữa tươi", "lít"],
          ["Đường phèn", "kg"],
          ["Trân châu đen", "kg"],
          ["Trà xanh Matcha", "kg"],
        ].map(([name, unit]) => (
          <div
            key={name}
            style={{
              display: "flex",
              justifyContent: "space-between",
              background: "#fff",
              borderRadius: 8,
              padding: "6px 12px",
              border: "1px solid #e8e8e8",
            }}
          >
            <Text>{name}</Text>
            <Tag color="cyan">{unit}</Tag>
          </div>
        ))}
      </Space>
    ),
  },
  {
    key: 2,
    icon: <ImportOutlined />,
    title: "Nhập kho nguyên vật liệu",
    subtitle: "Ghi nhận từng lần nhập hàng — ngày nhập phải chính xác",
    color: "#52c41a",
    bgColor: "#f6ffed",
    description:
      "Mỗi lần nhập hàng, tạo một phiếu nhập ghi rõ nguyên liệu, số lượng, đơn giá và ngày nhập. " +
      "Ngày nhập quyết định phiếu này thuộc kỳ báo cáo nào — vì vậy cần nhập đúng ngày thực tế nhận hàng.",
    actions: [
      "Vào menu Nhập kho → Tạo phiếu nhập",
      "Chọn ngày nhập thực tế",
      "Thêm từng nguyên liệu: chọn tên, điền số lượng và đơn giá",
      "Kiểm tra lại rồi nhấn Lưu phiếu nhập",
    ],
    warnings: [
      {
        type: "error",
        message:
          "Nếu hàng về đúng ngày kiểm kê (VD: ngày 10) — hàng đó thuộc kỳ tiếp theo. " +
          "Hãy nhập ngày hôm sau (ngày 11) để hệ thống tính đúng vào kỳ kế tiếp.",
      },
      {
        type: "info",
        message: "Có thể tạo nhiều phiếu nhập trong một ngày",
      },
    ],
    example: (
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 12,
          border: "1px solid #b7eb8f",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <Text strong>Phiếu nhập #001</Text>
          <Tag color="green">11/03/2025</Tag>
        </div>
        {[
          ["Trà đen Assam", "10 kg", "120,000đ", "1,200,000đ"],
          ["Sữa tươi", "20 lít", "30,000đ", "600,000đ"],
        ].map(([name, qty, price, total]) => (
          <div
            key={name}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr",
              fontSize: 12,
              padding: "4px 0",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Text>{name}</Text>
            <Text type="secondary">{qty}</Text>
            <Text type="secondary">{price}</Text>
            <Text strong style={{ color: "#52c41a" }}>
              {total}
            </Text>
          </div>
        ))}
      </div>
    ),
  },
  {
    key: 3,
    icon: <DollarOutlined />,
    title: "Nhập chi phí vận hành",
    subtitle: "Điện, nước, mặt bằng, lương nhân viên...",
    color: "#fa8c16",
    bgColor: "#fff7e6",
    description:
      "Ghi nhận tất cả chi phí phát sinh trong kỳ. Vì hệ thống tính báo cáo theo " +
      "khoảng thời gian giữa 2 lần kiểm kê, mỗi khoản chi cần được nhập đúng ngày " +
      "thực tế để rơi vào đúng kỳ cần tính.",
    actions: [
      "Vào menu Chi phí → Thêm chi phí",
      "Chọn loại chi phí: Điện / Nước / Mặt bằng / Nhân viên / Khác",
      "Nhập ngày phát sinh thực tế và số tiền",
      "Lặp lại cho tất cả khoản chi trong kỳ",
    ],
    warnings: [
      {
        type: "warning",
        message:
          "Lương nhân viên trả ngày 10 hàng tháng → nhập ngày chi phí là ngày 10 để tính đúng kỳ kết thúc ngày 10.",
      },
      {
        type: "info",
        message:
          "Các chi phí như tiền điện thường ra hoá đơn vào đầu tháng sau — nhập đúng ngày ghi trên hoá đơn.",
      },
    ],
    example: (
      <Space direction="vertical" style={{ width: "100%" }} size={4}>
        {[
          ["Mặt bằng", "5,000,000đ", "01/03", "warning"],
          ["Lương nhân viên", "8,000,000đ", "10/03", "gold"],
          ["Tiền điện", "800,000đ", "05/03", "orange"],
          ["Tiền nước", "150,000đ", "05/03", "orange"],
        ].map(([name, amount, date, color]) => (
          <div
            key={name}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#fff",
              borderRadius: 8,
              padding: "6px 12px",
              border: "1px solid #ffd591",
            }}
          >
            <Text>{name}</Text>
            <Space>
              <Tag color={color}>{date}</Tag>
              <Text strong style={{ color: "#fa8c16" }}>
                {amount}
              </Text>
            </Space>
          </div>
        ))}
      </Space>
    ),
  },
  {
    key: 4,
    icon: <AuditOutlined />,
    title: "Lập phiếu kiểm kê kho",
    subtitle: "Đếm tồn thực tế — chốt sổ kỳ hiện tại",
    color: "#722ed1",
    bgColor: "#f9f0ff",
    description:
      "Đếm và ghi lại số lượng tồn thực tế của tất cả nguyên liệu có trong kho. " +
      "Phiếu kiểm kê vừa đóng kỳ hiện tại (trở thành tồn cuối kỳ), vừa mở đầu cho kỳ tiếp theo " +
      "(trở thành tồn đầu kỳ sau). Nên chọn một ngày cố định mỗi tháng để việc đối soát nhất quán.",
    actions: [
      "Đảm bảo đã nhập đủ phiếu nhập kho và chi phí của kỳ này",
      "Tiến hành đếm tồn kho thực tế trong kho",
      "Vào menu Kiểm kê → Tạo phiếu kiểm kê",
      "Nhập đầy đủ tất cả nguyên liệu, điền số lượng tồn thực tế và đơn giá hiện tại",
      "Kiểm tra cảnh báo (nếu có) rồi nhấn Lưu",
    ],
    warnings: [
      {
        type: "warning",
        message:
          "Phải nhập đầy đủ TẤT CẢ nguyên liệu đã khai báo. Nguyên liệu nào không còn tồn thì điền số lượng = 0.",
      },
      {
        type: "info",
        message:
          "Nên chọn ngày cố định mỗi tháng (VD: ngày 10 — ngày trả lương). Ngày kiểm kê chính là ngày chốt kỳ báo cáo.",
      },
      {
        type: "error",
        message:
          "Hệ thống sẽ KHÔNG THỂ CẢNH BÁO nếu tồn nhập vào lớn hơn tổng lượng hàng có thể có — VÌ HỆ THỐNG KHÔNG KIỂM SOÁT TIÊU HAO NGUYÊN VẬT LIỆU, CHO NÊN HÃY KIỂM KÊ VÀ ĐIỀN SỐ LƯỢNG CHÍNH XÁC.",
      },
    ],
    example: (
      <div>
        <div
          style={{
            background: "#fff",
            borderRadius: 8,
            padding: 12,
            border: "1px solid #d3adf7",
            marginBottom: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <Text strong>Phiếu kiểm kê tháng 3</Text>
            <Tag color="purple">10/03/2025</Tag>
          </div>
          {[
            ["Trà đen Assam", "6 kg", "120,000đ", "720,000đ", "ok"],
            ["Sữa tươi", "5 lít", "30,000đ", "150,000đ", "ok"],
            ["Đường phèn", "0 kg", "25,000đ", "0đ", "warn"],
          ].map(([name, qty, price, total, status]) => (
            <div
              key={name}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1fr 20px",
                fontSize: 12,
                padding: "5px 0",
                borderTop: "1px solid #f0f0f0",
                alignItems: "center",
              }}
            >
              <Text>{name}</Text>
              <Text type="secondary">{qty}</Text>
              <Text type="secondary">{price}</Text>
              <Text strong style={{ color: "#722ed1" }}>
                {total}
              </Text>
              {status === "warn" ? (
                <Tooltip title="Tồn = 0, đã dùng hết">
                  <WarningFilled style={{ color: "#faad14", fontSize: 14 }} />
                </Tooltip>
              ) : (
                <CheckCircleFilled style={{ color: "#52c41a", fontSize: 14 }} />
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 16,
          }}
        >
          <Text type="secondary">Tổng giá trị tồn</Text>
          <Text strong style={{ color: "#722ed1", fontSize: 15 }}>
            870,000đ
          </Text>
        </div>
      </div>
    ),
  },
];

// ─── TIMELINE DATA ────────────────────────────────────────────────────────────

const TIMELINE_EXAMPLE = [
  { date: "10/02", label: "Kiểm kê tháng 1", type: "inventory", note: "Chốt kỳ T1, mở kỳ T2" },
  {
    date: "11/02",
    label: "Nhập kho (nếu nhập ngày 10 → đổi sang 11)",
    type: "import",
    note: "Tính vào kỳ T2",
  },
  {
    date: "01–09/03",
    label: "Nhập kho bình thường, ghi nhận chi phí",
    type: "normal",
    note: "Kỳ T2 đang chạy",
  },
  {
    date: "10/03",
    label: "Kiểm kê tháng 2 + Trả lương",
    type: "inventory",
    note: "Chốt kỳ T2 → tạo báo cáo",
  },
  { date: "11/03", label: "Nhập kho kỳ T3 bắt đầu", type: "import", note: "Tính vào kỳ T3" },
];

// ─── COMPONENTS ───────────────────────────────────────────────────────────────

function StepCard({
  step,
  active,
  onClick,
}: {
  step: StepDetail;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      hoverable
      onClick={onClick}
      style={{
        borderRadius: 16,
        border: active ? `2px solid ${step.color}` : "2px solid transparent",
        background: active ? step.bgColor : "#fafafa",
        transition: "all 0.25s ease",
        cursor: "pointer",
        boxShadow: active ? `0 4px 24px ${step.color}30` : "0 1px 4px #0000000a",
      }}
      bodyStyle={{ padding: "20px 24px" }}
    >
      <Space align="start" size={14}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: active ? step.color : "#e8e8e8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            color: active ? "#fff" : "#999",
            flexShrink: 0,
            transition: "all 0.25s",
          }}
        >
          {step.icon}
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <Tag
              color={active ? step.color : "default"}
              style={{ borderRadius: 20, fontSize: 11, margin: 0 }}
            >
              Bước {step.key + 1}
            </Tag>
          </div>
          <Text strong style={{ fontSize: 14, color: active ? step.color : "#333" }}>
            {step.title}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {step.subtitle}
          </Text>
        </div>
      </Space>
    </Card>
  );
}

function DetailPanel({ step }: { step: StepDetail }) {
  return (
    <div style={{ animation: "fadeIn 0.3s ease" }}>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }}`}</style>

      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${step.color}18, ${step.color}08)`,
          borderRadius: 16,
          padding: "24px 28px",
          border: `1px solid ${step.color}30`,
          marginBottom: 20,
        }}
      >
        <Space size={14} align="center">
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: step.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
              color: "#fff",
            }}
          >
            {step.icon}
          </div>
          <div>
            <Space size={8}>
              <Badge
                count={`Bước ${step.key + 1}`}
                style={{ background: step.color, borderRadius: 20 }}
              />
            </Space>
            <Title level={4} style={{ margin: "4px 0 0", color: step.color }}>
              {step.title}
            </Title>
            <Text type="secondary">{step.subtitle}</Text>
          </div>
        </Space>
      </div>

      {/* Description */}
      <Card
        style={{ borderRadius: 12, marginBottom: 16, border: "1px solid #f0f0f0" }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.8, color: "#444" }}>
          {step.description}
        </Paragraph>
      </Card>

      <Row gutter={16}>
        {/* Actions */}
        <Col xs={24} md={step.example ? 12 : 24}>
          <Card
            title={
              <Space>
                <CheckCircleFilled style={{ color: step.color }} />
                <Text strong>Các bước thực hiện</Text>
              </Space>
            }
            style={{ borderRadius: 12, border: "1px solid #f0f0f0", height: "100%" }}
            bodyStyle={{ padding: "12px 20px" }}
          >
            <Timeline
              items={step.actions.map((action, i) => ({
                dot: (
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: step.color,
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {i + 1}
                  </div>
                ),
                children: <Text style={{ fontSize: 13, lineHeight: 1.7 }}>{action}</Text>,
              }))}
            />
          </Card>
        </Col>

        {/* Example */}
        {step.example && (
          <Col xs={24} md={12}>
            <Card
              title={
                <Space>
                  <InfoCircleFilled style={{ color: step.color }} />
                  <Text strong>Ví dụ minh hoạ</Text>
                </Space>
              }
              style={{ borderRadius: 12, border: "1px solid #f0f0f0", height: "100%" }}
              bodyStyle={{ padding: "12px 20px" }}
            >
              {step.example}
            </Card>
          </Col>
        )}
      </Row>

      {/* Warnings */}
      {step.warnings && step.warnings.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            {step.warnings.map((w, i) => (
              <Alert
                key={i}
                type={w.type}
                showIcon
                message={w.message}
                style={{ borderRadius: 10 }}
              />
            ))}
          </Space>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

interface GuideProfitLossProps {
  setMenuItemSelected?: (value: string) => void;
}

function GuideProfitLoss(props: GuideProfitLossProps) {
  const { setMenuItemSelected } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [showTimeline, setShowTimeline] = useState(false);

  const current = STEPS[activeStep];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6fa",
        fontFamily: "'Be Vietnam Pro', 'Segoe UI', sans-serif",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a1f3a 0%, #2d3561 60%, #1a4a6e 100%)",
          padding: "32px 40px 28px",
          color: "#fff",
        }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Space size={16} align="center" style={{ marginBottom: 8 }}>
            <BarChartOutlined style={{ fontSize: 32, color: "#ffd666" }} />
            <div>
              <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                Hướng dẫn lập Báo cáo Lãi / Lỗ
              </Title>
              <Text style={{ color: "#a8b8d8", fontSize: 14 }}>
                Thực hiện đúng 5 bước theo thứ tự để có báo cáo chính xác
              </Text>
            </div>
          </Space>

          {/* Progress bar */}
          <div
            style={{
              marginTop: 20,
              background: "#ffffff20",
              borderRadius: 8,
              height: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${((activeStep + 1) / STEPS.length) * 100}%`,
                background: "linear-gradient(90deg, #52c41a, #ffd666)",
                borderRadius: 8,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <Text style={{ color: "#a8b8d8", fontSize: 12, marginTop: 4, display: "block" }}>
            Bước {activeStep + 1} / {STEPS.length}
          </Text>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 24px" }}>
        {/* Timeline Button */}
        <div style={{ marginBottom: 20, textAlign: "right" }}>
          <Button
            icon={<CalendarOutlined />}
            onClick={() => setShowTimeline(!showTimeline)}
            style={{ borderRadius: 8 }}
          >
            {showTimeline ? "Ẩn" : "Xem"} ví dụ timeline kỳ báo cáo
          </Button>
        </div>

        {/* Timeline Example */}
        {showTimeline && (
          <Card
            style={{
              borderRadius: 16,
              marginBottom: 24,
              border: "1px solid #d3adf7",
              background: "#faf7ff",
            }}
            bodyStyle={{ padding: "20px 28px" }}
          >
            <Space style={{ marginBottom: 16 }}>
              <ClockCircleOutlined style={{ color: "#722ed1", fontSize: 18 }} />
              <Text strong style={{ color: "#722ed1", fontSize: 15 }}>
                Ví dụ: Kỳ báo cáo tháng 3 (kiểm kê ngày 10 hàng tháng)
              </Text>
            </Space>
            <div style={{ display: "flex", alignItems: "center", gap: 0, overflowX: "auto" }}>
              {TIMELINE_EXAMPLE.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <div style={{ textAlign: "center", width: 140 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background:
                          item.type === "inventory"
                            ? "#722ed1"
                            : item.type === "import"
                              ? "#52c41a"
                              : "#1890ff",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 8px",
                        fontSize: 18,
                        fontWeight: 700,
                        boxShadow: item.type === "inventory" ? "0 0 0 4px #d3adf7" : undefined,
                      }}
                    >
                      {item.type === "inventory" ? (
                        <AuditOutlined />
                      ) : item.type === "import" ? (
                        <ImportOutlined />
                      ) : (
                        <DollarOutlined />
                      )}
                    </div>
                    <Text
                      strong
                      style={{
                        fontSize: 12,
                        color:
                          item.type === "inventory"
                            ? "#722ed1"
                            : item.type === "import"
                              ? "#389e0d"
                              : "#1890ff",
                        display: "block",
                      }}
                    >
                      {item.date}
                    </Text>
                    <Text
                      style={{
                        fontSize: 11,
                        color: "#555",
                        display: "block",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.label}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 2 }}>
                      {item.note}
                    </Text>
                  </div>
                  {i < TIMELINE_EXAMPLE.length - 1 && (
                    <ArrowRightOutlined
                      style={{ color: "#d9d9d9", fontSize: 16, margin: "0 4px" }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Bracket showing the period */}
            <Divider style={{ margin: "16px 0 8px" }} />
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Space>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#722ed1",
                  }}
                />
                <Text style={{ fontSize: 12 }}>Ngày kiểm kê — chốt kỳ</Text>
              </Space>
              <Space>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#52c41a",
                  }}
                />
                <Text style={{ fontSize: 12 }}>Nhập kho</Text>
              </Space>
              <Space>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#1890ff",
                  }}
                />
                <Text style={{ fontSize: 12 }}>Doanh thu & chi phí trong kỳ</Text>
              </Space>
            </div>

            <Alert
              style={{ marginTop: 12, borderRadius: 10 }}
              type="info"
              showIcon
              icon={<BulbOutlined />}
              message={
                <Text style={{ fontSize: 13 }}>
                  <strong>Kỳ tháng 2</strong> = từ ngày 11/02 đến 10/03. Doanh thu, nhập kho và chi
                  phí trong khoảng này sẽ được tổng hợp vào báo cáo tháng 2.
                </Text>
              }
            />
          </Card>
        )}

        {/* Step Navigation Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12,
            marginBottom: 24,
          }}
        >
          {STEPS.map((step) => (
            <StepCard
              key={step.key}
              step={step}
              active={activeStep === step.key}
              onClick={() => setActiveStep(step.key)}
            />
          ))}
        </div>

        {/* Detail Panel */}
        <DetailPanel step={current} />

        {/* Navigation Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 28,
            paddingTop: 20,
            borderTop: "1px solid #e8e8e8",
          }}
        >
          <Button
            size="large"
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => s - 1)}
            style={{ borderRadius: 10, minWidth: 120 }}
          >
            ← Bước trước
          </Button>

          <Space>
            {STEPS.map((s) => (
              <div
                key={s.key}
                onClick={() => setActiveStep(s.key)}
                style={{
                  width: activeStep === s.key ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: activeStep === s.key ? s.color : "#d9d9d9",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </Space>

          {activeStep < STEPS.length - 1 ? (
            <Button
              type="primary"
              size="large"
              onClick={() => setActiveStep((s) => s + 1)}
              style={{
                borderRadius: 10,
                minWidth: 160,
                background: current.color,
                borderColor: current.color,
              }}
            >
              Bước tiếp theo →
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              icon={<BarChartOutlined />}
              style={{
                borderRadius: 10,
                minWidth: 200,
                background: "linear-gradient(90deg, #52c41a, #1890ff)",
                border: "none",
              }}
              onClick={() => setMenuItemSelected?.("profit_loss")}
            >
              Xem Báo cáo Lãi / Lỗ
            </Button>
          )}
        </div>

        {/* Quick Summary */}
        <Collapse
          style={{ marginTop: 24, borderRadius: 12, border: "1px solid #e8e8e8" }}
          items={[
            {
              key: "summary",
              label: (
                <Space>
                  <ExclamationCircleFilled style={{ color: "#fa8c16" }} />
                  <Text strong>Tóm tắt các lưu ý quan trọng nhất</Text>
                </Space>
              ),
              children: (
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  {[
                    {
                      color: "#f5222d",
                      text: "Ngày nhập kho trùng ngày kiểm kê → đổi sang ngày hôm SAU để tính đúng kỳ tiếp theo.",
                    },
                    {
                      color: "#fa8c16",
                      text: "Chi phí vận hành nhập đúng ngày thực tế phát sinh để rơi đúng kỳ báo cáo.",
                    },
                    {
                      color: "#722ed1",
                      text: "Phiếu kiểm kê phải có ĐẦY ĐỦ tất cả nguyên liệu. Hết rồi thì điền 0, không được bỏ trống.",
                    },
                    {
                      color: "#1890ff",
                      text: "Chọn ngày kiểm kê cố định mỗi tháng (VD: ngày 10). Ngày này là mốc chốt kỳ báo cáo.",
                    },
                    {
                      color: "#52c41a",
                      text: "Kỳ báo cáo = khoảng giữa 2 lần kiểm kê, không phải đầu/cuối tháng calendar.",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        padding: "10px 14px",
                        background: `${item.color}08`,
                        borderRadius: 8,
                        borderLeft: `3px solid ${item.color}`,
                      }}
                    >
                      <Text style={{ color: item.color, fontWeight: 700, flexShrink: 0 }}>
                        {i + 1}.
                      </Text>
                      <Text style={{ fontSize: 13 }}>{item.text}</Text>
                    </div>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
export default GuideProfitLoss;
